import './Selectbox.less';
import './PhoneNumberInput.less';

import clsx from 'clsx';
import React, { FC, ReactNode, useState } from 'react';

import { concatErrors, makeField, NfInput, TOUCH_ON_BLUR_ONLY_IF_CHANGED } from 'nike-form-js';
import { useReframe } from 'nike-re-framejs';
import FieldErrors from './FieldErrors';
import i18n from '../../common/i18n';
import { Select } from './Select';

interface PhoneNumberInputProps {
    form?: any;
    path: string[];
    prefixPath: string[];
    label?: ReactNode;
    size?: string;
    hint?: string;
    active?: boolean;
    autocomplete?: string;
    className?: string;
    disabled?: boolean;
    phoneNumber?: string;
    touchOnChange?: boolean;
    touchOnBlur?: any;
    children?: ReactNode;
}

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
    form,
    path,
    prefixPath,
    label,
    size,
    hint,
    disabled = false,
    autocomplete = 'off',
    className,
    phoneNumber,
    touchOnChange = false,
    touchOnBlur = TOUCH_ON_BLUR_ONLY_IF_CHANGED,
    children,
    ...rest
}) => {
    const field = makeField(form, path, undefined);
    const fieldPrefix = makeField(form, prefixPath, undefined);
    const { deref } = useReframe();
    const isTouched = deref(field.isTouched());
    const isPrefixTouched = deref(fieldPrefix.isTouched());
    const errors = deref(field.getErrors());
    const isError = (isTouched || isPrefixTouched) && errors.size > 0;
    const id = '' + path;
    const [close, setOpen] = useState(false);

    return (
        <div
            data-path={path && path[0]}
            className={clsx(['nui-input p-rel mb-20 flex flex-col', disabled ? 'nui-input-disabled' : null, className])}
        >
            {label ? <div className="nui-input-label fs-14 text-extra-bold">{label}</div> : null}
            {hint ? <div className="nui-input-hint fs-12">{hint}</div> : null}
            <div className="nui-phone-number-input full-width flex flex-row items-center justify-between">
                <div className="nui-select p-rel mr-10" onClick={disabled ? null : () => setOpen(!close)}>
                    <Select
                        field={fieldPrefix}
                        options={[
                            { value: '+421', label: '+421' },
                            { value: '+36', label: '+36' },
                            { value: '+380', label: '+380' },
                            { value: '+48', label: '+48' }
                        ]}
                        disabled={disabled}
                    />
                </div>
                <div className="full-width">
                    <NfInput
                        field={field}
                        attrs={Object.assign({}, rest, {
                            id,
                            autoComplete: autocomplete,
                            disabled,
                            type: 'tel',
                            inputMode: 'numeric',
                            touchOnChange: touchOnChange,
                            touchOnBlur: touchOnBlur,
                            className: clsx('nui-native-input', size, { 'nui-input-error': isError })
                        })}
                    />
                </div>
            </div>
            {children}
            {isError ? <FieldErrors errors={errors} className /> : null}
        </div>
    );
};

export function splitPhone(phoneNumber) {
    const patternsArr = Object.keys(patterns);
    const prefix = patternsArr.find(element => phoneNumber.includes(element));

    if (prefix) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [prefixSplit, phoneSplit] = phoneNumber.split(prefix);
        return [prefix, phoneSplit];
    }
    return ['', phoneNumber];
}

// @ts-ignore
window.splitPhone = splitPhone;

const patterns = {
    '+421': /^9\d{8}$/,
    '+36': /^(?:20|30|31|62|70)\d{7}$/,
    '+420': /^(?:60|70|72|73|77|79|91)\d{7}$/,
    '+48': /^(?:50|51|53|57|60|66|69|72|73|78|79|88)\d{7}$/,
    '+380': /^\d{9}$/
};

function phoneNumberValidator(errors, value, path, pathPrefix) {
    const number = value.getIn(path);
    const prefix = value.getIn(pathPrefix);

    if (!number || number === '') {
        const error = i18n('validation.phone.required');
        return errors.updateIn(path, concatErrors([error]));
    }

    const pattern = patterns[prefix];
    if (number.match(pattern) === null) {
        const error = i18n('validation.phone.invalid');
        return errors.updateIn(path, concatErrors([error]));
    }
    return errors;
}

export function getPhoneNumberValidator(path, pathPrefix) {
    return (errors, value) => phoneNumberValidator(errors, value, path, pathPrefix);
}

export default PhoneNumberInput;
